// Export the rendered worksheet as PDF and PNG, and share via the system share sheet.

import { jsPDF } from 'jspdf';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';

function isCapacitorNative(): boolean {
  return typeof (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform === 'function'
    && (window as unknown as { Capacitor: { isNativePlatform: () => boolean } }).Capacitor.isNativePlatform();
}

/** Build a JPEG data URL from the rendered canvas. Quality 0.92 keeps file size sane. */
export function canvasToJpegDataUrl(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL('image/jpeg', 0.92);
}

/** Build a PNG data URL (lossless). */
export function canvasToPngDataUrl(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL('image/png');
}

/**
 * Build a PDF from the canvas. Single page sized to the canvas aspect ratio.
 * Returns a Blob URL the caller can pass to a download anchor or the share sheet.
 */
export function canvasToPdfBlob(canvas: HTMLCanvasElement): Blob {
  const jpegDataUrl = canvasToJpegDataUrl(canvas);
  const orientation = canvas.width > canvas.height ? 'landscape' : 'portrait';

  // Use millimetres so the PDF is print-friendly. We size the page to match the canvas
  // aspect ratio while keeping the long edge to a standard A4 dimension (297 mm long edge).
  const longEdgeMm = 297;
  const ratio = canvas.height / canvas.width;
  const pageWidthMm = orientation === 'portrait' ? longEdgeMm / Math.max(ratio, 1) : longEdgeMm;
  const pageHeightMm = orientation === 'portrait' ? longEdgeMm : longEdgeMm * (canvas.height / canvas.width);

  const pdf = new jsPDF({
    orientation,
    unit: 'mm',
    format: [pageWidthMm, pageHeightMm],
    compress: true,
  });

  pdf.addImage(jpegDataUrl, 'JPEG', 0, 0, pageWidthMm, pageHeightMm, undefined, 'FAST');
  return pdf.output('blob');
}

/** Trigger a browser download. Web only - on native we save and share via Capacitor. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadPdf(canvas: HTMLCanvasElement, filename = 'worksheet.pdf') {
  const blob = canvasToPdfBlob(canvas);
  if (isCapacitorNative()) {
    await saveAndShareOnNative(blob, filename, 'application/pdf');
    return;
  }
  downloadBlob(blob, filename);
}

export async function downloadPng(canvas: HTMLCanvasElement, filename = 'worksheet.png') {
  const dataUrl = canvasToPngDataUrl(canvas);
  const blob = await (await fetch(dataUrl)).blob();
  if (isCapacitorNative()) {
    await saveAndShareOnNative(blob, filename, 'image/png');
    return;
  }
  downloadBlob(blob, filename);
}

async function saveAndShareOnNative(blob: Blob, filename: string, mimeType: string) {
  const base64 = await blobToBase64(blob);
  const written = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
  });
  await Share.share({
    title: 'Worksheet',
    text: 'My finished worksheet',
    url: written.uri,
    dialogTitle: 'Save or share your worksheet',
    files: [written.uri],
  } as Parameters<typeof Share.share>[0]);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader result'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
